import { HttpMethod } from "@/types/https";
import { apiWorkchestraCall } from "../base";
import { ENDPOINT } from "../endpoints";
import { UserProfile } from "../types/user-profile.type";
import { useUserProfileStore } from "@/stores/user-profile-store";
import { userManager } from "../../../../lib/oidc-config";
import { SessionStatus } from "../types/session-status";

export const BASE_URL_WORKCHESTRA = window.location.origin;
const OIDC_STORAGE_KEY = `oidc.user:${BASE_URL_WORKCHESTRA}:WorkchestraWeb`;
const AUTH_COOKIE_NAME = "idsrv.session";
const LOGIN_URL = `${BASE_URL_WORKCHESTRA}/Identity/Login`;

const IS_STANDALONE = import.meta.env.VITE_STANDALONE_MODE === "true";

// Check if the user is authenticated by checking for the auth cookie
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    if (IS_STANDALONE) {
      const user = await userManager.getUser();
      return user !== null && !user.expired;
    } else {
      // Check if the auth cookie exists
      const cookies = document.cookie.split(";");
      return cookies.some((cookie) =>
        cookie.trim().startsWith(`${AUTH_COOKIE_NAME}=`)
      );
    }
  } catch (error) {
    console.error("Error checking authentication:", error);
    return false;
  }
};

// Redirect to the login page
export const redirectToLogin = async (): Promise<void> => {
  if (IS_STANDALONE) {
    const currentUrl =
      window.location.pathname + window.location.search + window.location.hash;
    await userManager.signinRedirect({ state: { returnUrl: currentUrl } });
  } else {
    window.location.href = LOGIN_URL;
  }
};

export const getDebugAccessToken = (): string | null => {
  const { setToken } = useUserProfileStore.getState();
  try {
    const oidcUser = sessionStorage.getItem(OIDC_STORAGE_KEY);
    if (oidcUser) {
      const user = JSON.parse(oidcUser);
      setToken(user.access_token || null);
      return user.access_token || null;
    }
  } catch (error) {
    console.error("Error retrieving access token:", error);
  }
  return null;
};

// Get access token
export const getAccessToken = async (): Promise<string | null> => {
  if (IS_STANDALONE) {
    const user = await userManager.getUser();
    return user?.access_token || null;
  } else {
    // Embedded mode: read from sessionStorage
    const oidcUser = sessionStorage.getItem(OIDC_STORAGE_KEY);
    if (oidcUser) {
      const user = JSON.parse(oidcUser);
      return user.access_token || null;
    }
  }
  return null;
};

// Logout
export const logout = async (): Promise<void> => {
  const returnUrl =
    window.location.pathname + window.location.search + window.location.hash;
  window.location.assign(`/logout?returnUrl=${encodeURIComponent(returnUrl)}`);
};

// Handle OIDC callback
export const handleCallback = async (): Promise<void> => {
  if (!IS_STANDALONE) {
    return;
  }

  const user = await userManager.signinRedirectCallback();
  sessionStorage.setItem("lastLoginTime", Date.now().toString());

  const returnUrl =
    (user?.state as { returnUrl?: string } | undefined)?.returnUrl ?? "/";
  window.location.replace(returnUrl);
};

export const fetchUserProfile = async (): Promise<UserProfile> => {
  const token = await getAccessToken();

  if (!token) throw new Error("No access token available");

  return apiWorkchestraCall<UserProfile>({
    url: ENDPOINT.USER_PROFILE,
    method: HttpMethod.GET,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getUserJobRoles = (jobOrderNo: string) => {
  return {
    name: `getUserJobRoles_${jobOrderNo}`,
    handle: (): Promise<any> =>
      apiWorkchestraCall<any>({
        url: ENDPOINT.JOB_ORDER_USER_ROLE + "/" + jobOrderNo,
        method: HttpMethod.GET,
      }),
  };
};

export const fetchSessionStatus = async (): Promise<SessionStatus> => {
  const token = await getAccessToken();

  if (!token) throw new Error("No access token available");

  return apiWorkchestraCall<SessionStatus>({
    url: ENDPOINT.SESSION_STATUS,
    method: HttpMethod.GET,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "Cache-Control": "no-store",
    },
    withCredentials: true,
  });
};
